import React from "react"

export default function ({
  containerRef,
  nextButtonRef,
  prevButtonRef,
}: {
  containerRef: React.RefObject<HTMLDivElement>
  nextButtonRef: React.RefObject<HTMLButtonElement>
  prevButtonRef: React.RefObject<HTMLButtonElement>
}): {
  scrollContainerBy: (scrollDirection: "right" | "left") => void
  scrollToIndex: (index: number) => void
} {
  React.useEffect(() => {
    const container = containerRef.current
    const nextButton = nextButtonRef.current
    const prevButton = prevButtonRef.current

    const getCarrouselPosition = () => {
      if (!container) return

      const containerWidth = container.getBoundingClientRect().width
      const childWith = container.children[0]?.getBoundingClientRect().width
      const scrollOffset = container.scrollLeft

      const paginationLimit = childWith / 3
      const reverseScrollOffset =
        container?.scrollWidth - scrollOffset - containerWidth
      if (prevButton) prevButton.disabled = scrollOffset < paginationLimit
      if (nextButton)
        nextButton.disabled = reverseScrollOffset < paginationLimit
    }
    getCarrouselPosition()
    container?.addEventListener("scroll", getCarrouselPosition)

    return () => {
      container?.scroll(0, 0)
      container?.removeEventListener("scroll", getCarrouselPosition)
    }
  }, [containerRef, nextButtonRef, prevButtonRef])

  const scrollToIndex = React.useCallback(
    index => {
      const container = containerRef.current

      if (!container) return
      const selectedChild = containerRef.current.children[index]

      if (!selectedChild) return
      selectedChild.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      })
    },
    [containerRef]
  )

  const scrollContainerBy = React.useCallback(
    (direction: "right" | "left") => {
      const container = containerRef.current
      if (!container) return
      const child = container.childNodes[0] as HTMLDivElement
      const { width } = child.getBoundingClientRect()
      return container.scrollBy({
        left: width * (direction === "right" ? 1 : -1),
        behavior: "smooth",
      })
    },
    [containerRef]
  )

  return { scrollContainerBy, scrollToIndex }
}
